import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
} from 'chart.js';
import {
    getLeagueRankingForProfessor,
    getAllPendingTransactions,
    getAllTransactionsByStatus,
    getTeamActivityStats,
} from '../../../Api/authApi.js.js';
import { formatPrice } from '../../../functions/price-formater.js';
import { Skeleton, SkeletonCircle } from '../../components/Skeleton';
import './equipes.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const PALETTE = ['#7AB8E0', '#9B7BE0', '#E0C281', '#7AE0C5', '#E07AAB', '#4ADE9E', '#FF9E7A'];

const nameColor = (name = '') => {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
    return PALETTE[h % PALETTE.length];
};

const initials = (name = '') =>
    name.split(/[\s._]+/).map(w => w[0] || '').join('').toUpperCase().slice(0, 2) || '?';

const fmtPct = (n) => {
    const v = parseFloat(n) || 0;
    return (v >= 0 ? '+' : '') + v.toFixed(2) + ' %';
};

function Avatar({ name, size = 34 }) {
    const color = nameColor(name);
    return (
        <div style={{
            width: size, height: size, borderRadius: '50%', flexShrink: 0,
            background: `linear-gradient(155deg, ${color}33, ${color}14)`,
            border: `1px solid ${color}55`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: size * 0.4, fontWeight: 700, color,
        }}>{initials(name)}</div>
    );
}

function ChangeText({ value, size = 13 }) {
    const v = parseFloat(value) || 0;
    return (
        <span className="mono num" style={{ color: v >= 0 ? 'var(--up)' : 'var(--down)', fontSize: size, fontWeight: 600 }}>
            {fmtPct(v)}
        </span>
    );
}

function Chevron({ open }) {
    return (
        <span style={{ color: 'var(--text-mute)', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s', flexShrink: 0 }}>
            <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 6l6 6-6 6" />
            </svg>
        </span>
    );
}

// ── Actifs détenus + historique d'un membre (chargés à l'ouverture de la ligne) ──

const HISTORY_PAGE_SIZE = 5;

function MemberDetail({ member }) {
    const [held, setHeld] = useState(null);
    const [history, setHistory] = useState(null);
    const [showAllHistory, setShowAllHistory] = useState(false);

    useEffect(() => {
        let cancelled = false;
        getAllPendingTransactions(member.user_id)
            .then(data => { if (!cancelled) setHeld(data?.transactions_pending || []); })
            .catch(() => { if (!cancelled) setHeld([]); });
        getAllTransactionsByStatus(member.user_id, { includeArchived: true })
            .then(data => {
                if (cancelled) return;
                const sorted = (data?.transactions || [])
                    .slice()
                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                setHistory(sorted);
            })
            .catch(() => { if (!cancelled) setHistory([]); });
        return () => { cancelled = true; };
    }, [member.user_id]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '0 0 10px 10px' }} className="py-3 px-3.5 xl:ps-[3.875rem]">
            <div>
                <div className="mono" style={{ fontSize: 11, color: 'var(--text-mute)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                    Actifs détenus
                </div>
                {held === null ? (
                    <span style={{ fontSize: 12.5, color: 'var(--text-mute)' }}>Chargement…</span>
                ) : held.length === 0 ? (
                    <span style={{ fontSize: 12.5, color: 'var(--text-mute)' }}>Aucune position ouverte.</span>
                ) : (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {held.map(h => (
                            <div key={h.unique_id} style={{
                                display: 'flex', alignItems: 'center', gap: 7,
                                padding: '6px 10px', borderRadius: 8,
                                background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)',
                            }}>
                                <span style={{
                                    fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 4,
                                    background: h.transaction_type === 'achat' ? 'var(--up-soft)' : 'var(--down-soft)',
                                    color: h.transaction_type === 'achat' ? 'var(--up)' : 'var(--down)',
                                    textTransform: 'uppercase',
                                }}>{h.transaction_type === 'achat' ? 'Long' : 'Short'}</span>
                                <span className="mono" style={{ fontSize: 12.5, fontWeight: 600 }}>{h.market_name || h.market_type}</span>
                                <span className="mono" style={{ fontSize: 11.5, color: 'var(--text-mute)' }}>×{parseFloat(h.quantity).toFixed(3)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div>
                <div className="mono" style={{ fontSize: 11, color: 'var(--text-mute)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                    Historique
                </div>
                {history === null ? (
                    <span style={{ fontSize: 12.5, color: 'var(--text-mute)' }}>Chargement…</span>
                ) : history.length === 0 ? (
                    <span style={{ fontSize: 12.5, color: 'var(--text-mute)' }}>Aucun historique disponible.</span>
                ) : (
                    <>
                        <div style={showAllHistory ? { maxHeight: 350, overflowY: 'auto', paddingRight: 10 } : undefined}>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                {(showAllHistory ? history : history.slice(0, HISTORY_PAGE_SIZE)).map((t, i, arr) => {
                                    const pnl = t.gain_loss != null ? parseFloat(t.gain_loss) : null;
                                    const date = t.created_at
                                        ? new Date(t.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
                                        : '';
                                    return (
                                        <div key={t.unique_id || i} style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                                            padding: '7px 0', borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none',
                                        }}>
                                            <span style={{ fontSize: 12.5 }}>
                                                {t.transaction_type === 'achat' ? 'Achat' : 'Vente'}{t.market_name ? ` · ${t.market_name}` : ''}
                                                <span style={{ color: 'var(--text-mute)' }}> — {date}</span>
                                            </span>
                                            <span style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexShrink: 0 }}>
                                                {t.montant != null && (
                                                    <span className="mono num" style={{ fontSize: 11.5, color: 'var(--text-mute)' }}>
                                                        Investi {formatPrice(t.montant)}
                                                    </span>
                                                )}
                                                {pnl != null && (
                                                    <span className="mono num" style={{ fontSize: 12, fontWeight: 600, color: pnl >= 0 ? 'var(--up)' : 'var(--down)' }}>
                                                        {pnl >= 0 ? '+' : ''}{formatPrice(pnl)}
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        {history.length > HISTORY_PAGE_SIZE && (
                            <button
                                type="button"
                                className="pill-tap"
                                onClick={() => setShowAllHistory(v => !v)}
                                style={{
                                    marginTop: 8, padding: 0, border: 'none', background: 'none', cursor: 'pointer',
                                    fontSize: 12.5, fontWeight: 600, color: 'var(--text-dim)',
                                    textDecoration: 'underline', textUnderlineOffset: 3,
                                }}
                            >
                                {showAllHistory ? 'Voir moins' : `Voir tout (${history.length})`}
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

function EquipesSkeleton() {
    return (
        <div className="prof-equipes-layout" style={{ maxWidth: '1160px' }}>
            <div className="relief" style={{ padding: 8, borderRadius: 16 }}>
                <div className="prof-equipes-team-list">
                    {[0, 1, 2, 3].map(i => (
                        <div key={i} style={{
                            padding: '11px 12px', borderRadius: 10, flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                        }}>
                            <Skeleton width={90} height={14} radius={4} />
                            <Skeleton width={16} height={11.5} radius={4} />
                        </div>
                    ))}
                </div>
            </div>

            <div>
                <div className="relief" style={{ padding: 18, borderRadius: 16, marginBottom: 14 }}>
                    <div className="flex flex-col xl:flex-row justify-between gap-4 xl:gap-6">
                        <div>
                            <div style={{ marginBottom: 3 }}><Skeleton width={140} height={19} radius={4} /></div>
                            <Skeleton width={180} height={12.5} radius={4} />
                        </div>
                        <div className="flex flex-col xl:flex-row gap-4 xl:gap-7">
                            {[0, 1, 2].map(i => (
                                <div key={i} className="flex flex-row-reverse xl:block gap-6 justify-between items-center">
                                    <div style={{ marginBottom: 4 }}><Skeleton width={60} height={18} radius={4} /></div>
                                    <Skeleton width={90} height={11.5} radius={4} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="relief" style={{ padding: 4, borderRadius: 16 }}>
                    {[0, 1, 2, 3].map(i => (
                        <div key={i} style={{
                            display: 'flex', alignItems: 'center', gap: 14, padding: '13px 14px',
                            borderBottom: i < 3 ? '1px solid var(--border)' : 'none',
                        }}>
                            <SkeletonCircle size={34} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ marginBottom: 6 }}><Skeleton width={110} height={14} radius={4} /></div>
                                <Skeleton width={140} height={11.5} radius={4} />
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <div style={{ marginBottom: 4 }}><Skeleton width={70} height={14.5} radius={4} /></div>
                                <Skeleton width={45} height={12} radius={4} style={{ marginLeft: 'auto' }} />
                            </div>
                            <Skeleton width={15} height={15} radius={4} />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// ── Modal "Stats d'activité" : nombre de positions ouvertes par jour, par joueur et pour l'équipe ──

const dayLabel = (isoDate) => {
    const d = new Date(isoDate + 'T00:00:00');
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

function TeamActivityModal({ team, onClose }) {
    const [status, setStatus] = useState('loading'); // loading | ready | empty | error
    const [stats, setStats] = useState(null);

    useEffect(() => {
        if (!team) return;
        let cancelled = false;
        setStatus('loading');
        setStats(null);
        getTeamActivityStats(team.team_id)
            .then(data => {
                if (cancelled) return;
                setStats(data);
                setStatus((data?.days || []).length ? 'ready' : 'empty');
            })
            .catch(() => { if (!cancelled) setStatus('error'); });
        return () => { cancelled = true; };
    }, [team]);

    if (!team) return null;

    const chartData = stats ? {
        labels: stats.days.map(dayLabel),
        datasets: [
            {
                label: 'Équipe',
                data: stats.team_series,
                borderColor: '#FFFFFF',
                backgroundColor: '#FFFFFF',
                borderWidth: 2.5,
                pointRadius: 0,
                pointHoverRadius: 4,
                tension: 0.25,
            },
            ...stats.players.map(p => {
                const color = nameColor(p.username);
                return {
                    label: p.username,
                    data: p.series,
                    borderColor: color,
                    backgroundColor: color,
                    borderWidth: 1.75,
                    pointRadius: 0,
                    pointHoverRadius: 3,
                    tension: 0.25,
                };
            }),
        ],
    } : null;

    const maxSeriesValue = stats
        ? Math.max(0, ...stats.team_series, ...stats.players.flatMap(p => p.series))
        : 0;

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: {
                display: true,
                position: 'top',
                align: 'center',
                labels: {
                    boxWidth: 8, boxHeight: 8, usePointStyle: true, pointStyle: 'circle',
                    color: '#FFFFFF', font: { size: 11.5 }, padding: 16
                },
            },
            tooltip: {
                backgroundColor: '#0E1830',
                borderColor: 'rgba(255,255,255,0.1)',
                borderWidth: 1,
                titleColor: '#FFFFFF',
                bodyColor: '#FFFFFF',
                padding: 10,
            },
        },
        scales: {
            x: {
                ticks: { color: '#FFFFFF', font: { size: 11 }, maxRotation: 0, autoSkip: true, maxTicksLimit: 8 },
                grid: { display: false },
            },
            y: {
                min: 0,
                max: maxSeriesValue > 0 ? maxSeriesValue : 1,
                ticks: { color: '#FFFFFF', font: { size: 11 }, precision: 0 },
                grid: { color: 'rgba(255,255,255,0.05)' },
            },
        },
    };

    return createPortal(
        <>
            <style>{`
                @keyframes teamActivityIn {
                    from { opacity: 0; transform: translateY(24px) scale(0.97); }
                    to   { opacity: 1; transform: translateY(0)     scale(1);    }
                }
            `}</style>
            <div
                onClick={onClose}
                style={{
                    position: 'fixed', inset: 0, zIndex: 9999, overflowY: 'auto',
                    background: 'rgba(5,7,18,0.62)', backdropFilter: 'blur(7px)', WebkitBackdropFilter: 'blur(7px)',
                }}
            >
                <div style={{ minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}>
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            width: '100%', maxWidth: 680, height: '90vh', maxHeight: '90vh', overflowY: 'auto',
                            display: 'flex', flexDirection: 'column',
                            borderRadius: 24,
                            background: 'linear-gradient(180deg, rgba(24,39,68,0.38) 0%, #0E1830 38%), #14213D',
                            border: '1px solid rgba(255,255,255,0.07)',
                            boxShadow: '0 30px 70px -22px rgba(0,0,0,0.75), inset 0 1px 0 rgba(255,255,255,0.07)',
                            padding: 20,
                            color: '#FFFFFF',
                            animation: 'teamActivityIn .32s cubic-bezier(0.16, 1, 0.3, 1)',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, flexShrink: 0 }}>
                            <div>
                                <div style={{ fontSize: 11, letterSpacing: '0.08em', color: '#FFFFFF', textTransform: 'uppercase', marginBottom: 4 }}>
                                    Stats d'activité
                                </div>
                                <div style={{ fontSize: 17, fontWeight: 600, color: '#FFFFFF' }}>{team.team_name}</div>
                            </div>
                            <button
                                onClick={onClose}
                                aria-label="Fermer"
                                style={{
                                    width: 32, height: 32, borderRadius: 16, background: 'rgba(255,255,255,0.06)',
                                    border: '1px solid rgba(255,255,255,0.07)', color: '#FFFFFF', cursor: 'pointer',
                                    display: 'grid', placeItems: 'center', padding: 0, flexShrink: 0,
                                }}
                            >
                                <svg width="12" height="12" viewBox="0 0 12 12">
                                    <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                                </svg>
                            </button>
                        </div>
                        <div style={{ fontSize: 12, color: '#FFFFFF', marginBottom: 16, flexShrink: 0 }}>
                            Positions ouvertes par jour, depuis le début de la ligue
                        </div>

                        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingTop: 32, paddingBottom: 32 }}>
                            {status === 'loading' && (
                                <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFFFFF', fontSize: 13, flexShrink: 0 }}>
                                    Chargement…
                                </div>
                            )}
                            {status === 'empty' && (
                                <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFFFFF', fontSize: 13, flexShrink: 0 }}>
                                    Pas encore de données pour cette équipe.
                                </div>
                            )}
                            {status === 'error' && (
                                <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFFFFF', fontSize: 13, flexShrink: 0 }}>
                                    Impossible de charger les stats d'activité.
                                </div>
                            )}
                            {status === 'ready' && chartData && (
                                <div style={{ height: 400, position: 'relative', flexShrink: 0 }}>
                                    <Line data={chartData} options={chartOptions} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
}

const Equipes = () => {
    const professeurId = useSelector((state) => state.auth.user?.id);
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedTeamId = searchParams.get('team');

    const [teams, setTeams] = useState([]);
    const [selectedTeamId, setSelectedTeamId] = useState(null);
    const [expandedMemberId, setExpandedMemberId] = useState(null);
    const [status, setStatus] = useState('loading'); // loading | ready | empty | error
    const [activityModalTeam, setActivityModalTeam] = useState(null);

    const load = useCallback(() => {
        if (!professeurId) return;
        setStatus('loading');
        getLeagueRankingForProfessor(professeurId)
            .then(data => {
                const rankedTeams = [...(data?.teams || [])].sort(
                    (a, b) => (parseFloat(b.total_montant) || 0) - (parseFloat(a.total_montant) || 0)
                );
                const requestedTeam = rankedTeams.find(t => String(t.team_id) === requestedTeamId);
                setTeams(rankedTeams);
                setSelectedTeamId(prev => prev || requestedTeam?.team_id || rankedTeams[0]?.team_id || null);
                setStatus(rankedTeams.length ? 'ready' : 'empty');
            })
            .catch(() => setStatus('error'));
    }, [professeurId, requestedTeamId]);

    useEffect(() => { load(); }, [load]);

    const selectedTeam = teams.find(t => t.team_id === selectedTeamId) || teams[0];
    const selectedTeamRank = teams.findIndex(t => t.team_id === selectedTeam?.team_id) + 1;
    const teamCapital = (selectedTeam?.members || []).reduce((s, m) => s + (parseFloat(m.capital) || 0), 0);

    return (
        <div className="prof-equipes-page">
            <div style={{ padding: '4px 4px 4px' }}>
                <div className="mono" style={{ fontSize: 11, letterSpacing: '0.08em', color: 'var(--text-mute)', textTransform: 'uppercase', marginBottom: 6 }}>
                    Suivi de session
                </div>
                <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: '-0.01em' }}>Équipes</h1>
            </div>

            {status === 'loading' && <EquipesSkeleton />}
            {status === 'empty' && (
                <div className="relief" style={{ marginTop: 16, borderRadius: 16, padding: 20, textAlign: 'center', color: 'var(--text-mute)', fontSize: 13 }}>
                    Aucune équipe trouvée pour votre ligue.
                </div>
            )}
            {status === 'error' && (
                <div className="relief" style={{ marginTop: 16, borderRadius: 16, padding: 20, textAlign: 'center', color: 'var(--down)', fontSize: 13 }}>
                    Impossible de charger les équipes de la ligue.
                </div>
            )}

            {status === 'ready' && selectedTeam && (
                <div className="prof-equipes-layout" style={{maxWidth: '1160px'}}>
                    {/* Liste des équipes */}
                    <div className="relief" style={{ padding: 8, borderRadius: 16 }}>
                        <div className="prof-equipes-team-list">
                            {teams.map(t => (
                                <div key={t.team_id} onClick={() => {
                                    setSelectedTeamId(t.team_id);
                                    setExpandedMemberId(null);
                                    setSearchParams({ team: String(t.team_id) }, { replace: true });
                                }}
                                    className={'pill-tap' + (t.team_id === selectedTeam.team_id ? '' : ' hover:!bg-black/[0.1]')}
                                    style={{
                                        padding: '11px 12px', borderRadius: 10, cursor: 'pointer', flexShrink: 0,
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                                        background: t.team_id === selectedTeam.team_id ? 'rgba(255,255,255,0.06)' : 'transparent',
                                    }}>
                                    <span style={{
                                        fontSize: 14, whiteSpace: 'nowrap',
                                        fontWeight: t.team_id === selectedTeam.team_id ? 600 : 500,
                                        color: t.team_id === selectedTeam.team_id ? 'var(--text)' : 'var(--text-dim)',
                                    }}>{t.team_name}</span>
                                    <span className="mono" style={{ fontSize: 11.5, color: 'var(--text-mute)' }}>{t.members.length}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Détail équipe */}
                    <div>
                        <div className="relief" style={{ padding: 18, borderRadius: 16, marginBottom: 14 }}>
                            <div className="flex flex-col xl:flex-row justify-between gap-4 xl:gap-6">
                                <div>
                                    <div style={{ fontSize: 19, fontWeight: 600, marginBottom: 3 }}>{selectedTeam.team_name}</div>
                                    <div style={{ fontSize: 12.5, color: 'var(--text-mute)' }}>
                                        Rang {selectedTeamRank} sur {teams.length} · {selectedTeam.members.length} élève{selectedTeam.members.length > 1 ? 's' : ''}
                                    </div>
                                    <button
                                        type="button"
                                        className="pill-tap"
                                        onClick={() => setActivityModalTeam(selectedTeam)}
                                        style={{
                                            marginTop: 6, padding: 0, border: 'none', background: 'none', cursor: 'pointer',
                                            fontSize: 12.5, fontWeight: 600, color: 'var(--text-dim)',
                                            textDecoration: 'underline', textUnderlineOffset: 3,
                                        }}
                                    >
                                        Stats d'activité
                                    </button>
                                </div>
                                <div className="flex flex-col xl:flex-row gap-4 xl:gap-7">
                                    <div className="flex flex-row-reverse xl:block gap-6 justify-between items-center">
                                        <div className="mono num" style={{ fontSize: 18, fontWeight: 700 }}>{formatPrice(teamCapital)}</div>
                                        <div style={{ fontSize: 11.5, color: 'var(--text-mute)' }}>Capital total</div>
                                    </div>
                                    <div className="flex flex-row-reverse xl:block gap-6 justify-between items-center">
                                        <ChangeText value={selectedTeam.total_montant} size={18} />
                                        <div style={{ fontSize: 11.5, color: 'var(--text-mute)' }}>Performance moyenne</div>
                                    </div>
                                    <div className="flex flex-row-reverse xl:block gap-6 justify-between items-center">
                                        <div className="mono num" style={{ fontSize: 18, fontWeight: 700 }}>{selectedTeam.open_positions_count ?? 0}</div>
                                        <div style={{ fontSize: 11.5, color: 'var(--text-mute)' }}>Position{selectedTeam.open_positions_count !== 1 ? 's' : ''} ouverte{selectedTeam.open_positions_count !== 1 ? 's' : ''}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="relief" style={{ padding: 4, borderRadius: 16 }}>
                            {selectedTeam.members.length === 0 && (
                                <div className="p-5 text-sm opacity-50">Aucun élève dans cette équipe.</div>
                            )}
                            {selectedTeam.members.map((m, i) => {
                                const open = expandedMemberId === m.user_id;
                                return (
                                    <div key={m.user_id}>
                                        <div className="prof-equipes-member-row pill-tap" onClick={() => setExpandedMemberId(open ? null : m.user_id)}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: 14, padding: '13px 14px', cursor: 'pointer',
                                                borderBottom: (i < selectedTeam.members.length - 1 || open) ? '1px solid var(--border)' : 'none',
                                            }}>
                                            <Avatar name={m.username} />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {m.username}
                                                </div>
                                                {!open && (
                                                    <div style={{ fontSize: 11.5, color: 'var(--text-mute)' }}>
                                                        {m.active_today ? "actif aujourd'hui" : "inactif aujourd'hui"} · {m.open_positions_count ?? 0} position{(m.open_positions_count ?? 0) > 1 ? 's' : ''}
                                                    </div>
                                                )}
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <div className="mono num" style={{ fontSize: 14.5, fontWeight: 700 }}>{formatPrice(m.capital)}</div>
                                                <ChangeText value={m.montant_individuel} size={12} />
                                            </div>
                                            <Chevron open={open} />
                                        </div>
                                        {open && <MemberDetail member={m} />}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            <TeamActivityModal team={activityModalTeam} onClose={() => setActivityModalTeam(null)} />
        </div>
    );
};

export default Equipes;
